import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import DashboardHome from "@/pages/DashboardHome";

// Code-splitting : chaque page est chargée à la demande (bundle initial plus léger, navigation plus rapide).
const ClientDevisValidationPage = lazy(() => import("@/pages/ClientDevisValidationPage"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const Confidentialite = lazy(() => import("@/pages/Confidentialite"));
const PresentationPage = lazy(() => import("@/pages/PresentationPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const SocietesPage = lazy(() => import("@/pages/SocietesPage"));
const SocieteDetailPage = lazy(() => import("@/pages/SocieteDetailPage"));
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const ManagersPage = lazy(() => import("@/pages/ManagersPage"));
const ResiliesPage = lazy(() => import("@/pages/ResiliesPage"));
const NouveauxClientsPage = lazy(() => import("@/pages/NouveauxClientsPage"));
const SuiviClesPage = lazy(() => import("@/pages/SuiviClesPage"));
const NfcPointsPage = lazy(() => import("@/pages/NfcPointsPage"));
const ClesEnvoyeesPage = lazy(() => import("@/pages/ClesEnvoyeesPage"));
const ConsignesPage = lazy(() => import("@/pages/ConsignesPage"));
const PassationPage = lazy(() => import("@/pages/PassationPage"));
const PresencePage = lazy(() => import("@/pages/PresencePage"));
const InterventionPage = lazy(() => import("@/pages/InterventionPage"));
const InterventionsPage = lazy(() => import("@/pages/InterventionsPage"));
const GardiennagePage = lazy(() => import("@/pages/GardiennagePage"));
const SuiviGardiennagePage = lazy(() => import("@/pages/SuiviGardiennagePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const EspaceEntreprisePage = lazy(() => import("@/pages/EspaceEntreprisePage"));
const EspacesPage = lazy(() => import("@/pages/EspacesPage"));
const PTIPage = lazy(() => import("@/pages/PTIPage"));
const AgentPTIPage = lazy(() => import("@/pages/AgentPTIPage"));
const MesMissionsPage = lazy(() => import("@/pages/MesMissionsPage"));
const MissionsCloturees = lazy(() => import("@/pages/MissionsCloturees"));
const AgentHomePage = lazy(() => import("@/pages/AgentHomePage"));
const ModInterventionPage = lazy(() => import("@/pages/ModInterventionPage"));
const ModRondePage = lazy(() => import("@/pages/ModRondePage"));
const SuiviRondesPage = lazy(() => import("@/pages/SuiviRondesPage"));
const RondesPage = lazy(() => import("@/pages/RondesPage"));
const DemoAccountPage = lazy(() => import("@/pages/DemoAccountPage"));
const EmailLogsPage = lazy(() => import("@/pages/EmailLogsPage"));
const PaieCcnPage = lazy(() => import("@/pages/PaieCcnPage"));
const CongesPage = lazy(() => import("@/pages/CongesPage"));
const PaiePage = lazy(() => import("@/pages/PaiePage"));
const PlanningPage = lazy(() => import("@/pages/PlanningPage"));
const DevisPage = lazy(() => import("@/pages/DevisPage"));
const ProspectionPage = lazy(() => import("@/pages/ProspectionPage"));
const NotesFraisPage = lazy(() => import("@/pages/NotesFraisPage"));
const MesNotesFraisPage = lazy(() => import("@/pages/MesNotesFraisPage"));
const ReclamationsPage = lazy(() => import("@/pages/ReclamationsPage"));
const AppelsOffresPage = lazy(() => import("@/pages/AppelsOffresPage"));
const MesReclamationsPage = lazy(() => import("@/pages/MesReclamationsPage"));
const AmendesPage = lazy(() => import("@/pages/AmendesPage"));
const APayerPage = lazy(() => import("@/pages/APayerPage"));
const TvaPage = lazy(() => import("@/pages/TvaPage"));
const ContratsPage = lazy(() => import("@/pages/ContratsPage"));
const ContratSignerPage = lazy(() => import("@/pages/ContratSignerPage"));
const RapportsMensuelsPage = lazy(() => import("@/pages/RapportsMensuelsPage"));
const ClientComptesRendusPage = lazy(() => import("@/pages/ClientComptesRendusPage"));
const TachesPage = lazy(() => import("@/pages/TachesPage"));
const RendezVousPage = lazy(() => import("@/pages/RendezVousPage"));
const MesAmendesPage = lazy(() => import("@/pages/MesAmendesPage"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const RelancesPage = lazy(() => import("@/pages/RelancesPage"));
const AssistantPage = lazy(() => import("@/pages/AssistantPage"));
const ChefPostePage = lazy(() => import("@/pages/ChefPostePage"));
const ChefAgentsPage = lazy(() => import("@/pages/ChefAgentsPage"));
const NotificationsAdminPage = lazy(() => import("@/pages/NotificationsAdminPage"));
const ChefsPostePage = lazy(() => import("@/pages/ChefsPostePage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const SousTraitantsPage = lazy(() => import("@/pages/SousTraitantsPage"));
const EntreprisesPage = lazy(() => import("@/pages/EntreprisesPage"));
const DotationsPage = lazy(() => import("@/pages/DotationsPage"));
const MesDotationsPage = lazy(() => import("@/pages/MesDotationsPage"));
const VehiculesPage = lazy(() => import("@/pages/VehiculesPage"));
const CarnetBordPage = lazy(() => import("@/pages/CarnetBordPage"));
const CarnetBordManagerPage = lazy(() => import("@/pages/CarnetBordManagerPage"));
const CompanyDocumentsPage = lazy(() => import("@/pages/CompanyDocumentsPage"));
const FacturationMensuellePage = lazy(() => import("@/pages/FacturationMensuellePage"));
const FacturationGardiennagePage = lazy(() => import("@/pages/FacturationGardiennagePage"));
const ControleDevisPage = lazy(() => import("@/pages/ControleDevisPage"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));
const ClientsPortailPage = lazy(() => import("@/pages/ClientsPortailPage"));
const ClientPortailSitesPage = lazy(() => import("@/pages/ClientPortailSitesPage"));
const ClientSiteDetailPage = lazy(() => import("@/pages/ClientSiteDetailPage"));
import { TABS } from "@/lib/tabs";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/login"); return; }
    const sessionId = decodeURIComponent(match[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (data.token) localStorage.setItem("auth_token", data.token);
        setUser(data);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.platform_role === "super_admin") return <Navigate to="/dashboard/entreprises" replace />;
  if (user?.role === "manager" || user?.role === "admin" || user?.role === "demo") {
    const tabs = user?.tab_permissions;
    if (user?.role === "admin" || user?.role === "demo" || !Array.isArray(tabs) || tabs.includes("dashboard")) return <DashboardHome />;
    const first = TABS.find((t) => !t.adminOnly && tabs.includes(t.key));
    return <Navigate to={first ? first.path : "/dashboard/profil"} replace />;
  }
  if (user?.role === "gardiennage" || user?.role === "agent") {
    const mods = user?.agent_modules || [];
    const path = { gardiennage: "/dashboard/mod/gardiennage", intervention: "/dashboard/mod/intervention", ronde: "/dashboard/mod/ronde" };
    const first = ["gardiennage", "intervention", "ronde"].find((m) => mods.includes(m));
    return <Navigate to={first ? path[first] : "/dashboard/profil"} replace />;
  }
  if (user?.role === "chef_poste") return <Navigate to="/dashboard/chef" replace />;
  if (user?.role === "secretaire") return <Navigate to="/dashboard/devis" replace />;
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  if ((user?.permissions || []).includes("clients")) return <Navigate to="/dashboard/clients" replace />;
  return <Navigate to="/dashboard/intervention" replace />;
}

function ModuleRoute({ module, tab, children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  if (user?.role === "chef_poste" && module !== "consignes") return <Navigate to="/dashboard/chef" replace />;
  const _agentMods = user?.agent_modules || [];
  const _isModAgent = user?.role === "agent" || user?.role === "gardiennage";
  const _agentExtraOk = _isModAgent && ((module === "clients" && _agentMods.includes("intervention")) || (module === "consignes" && _agentMods.length > 0));
  if (user?.role === "gardiennage" && !_agentExtraOk) return <Navigate to="/dashboard/gardiennage" replace />;
  if (user?.role === "agent" && !(user?.permissions || []).includes(module) && !_agentExtraOk) return <Navigate to="/dashboard/intervention" replace />;
  if (user?.role === "manager") {
    const tabs = user?.tab_permissions;
    if (Array.isArray(tabs) && !tabs.includes(tab || module)) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function DevisRoute({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  if (user?.role === "admin" || user?.role === "secretaire") return children;
  if (user?.role === "manager" && (user?.tab_permissions || []).includes("devis")) return children;
  return <Navigate to="/dashboard" replace />;
}

function MgrRoute({ children }) {
  const { user } = useAuth();
  return ["admin", "manager", "demo"].includes(user?.role) ? children : <Navigate to="/dashboard" replace />;
}

function SecRoute({ children }) {
  const { user } = useAuth();
  const ok = ["admin", "demo"].includes(user?.role) || (user?.role === "manager" && user?.acces_secretariat);
  return ok ? children : <Navigate to="/dashboard" replace />;
}

function TabRoute({ tab, children }) {
  const { user } = useAuth();
  if (user?.role === "chef_poste") return tab === "planning" ? children : <Navigate to="/dashboard/chef" replace />;
  if (user?.role !== "admin" && user?.role !== "manager" && user?.role !== "demo") return <Navigate to="/dashboard" replace />;
  if (user?.role === "manager") {
    const tabs = user?.tab_permissions;
    if (Array.isArray(tabs) && !tabs.includes(tab)) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function GardiennageRoute({ children }) {  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  if (user?.role === "chef_poste") return <Navigate to="/dashboard/chef" replace />;
  if (["gardiennage", "agent", "manager", "admin"].includes(user?.role)) return children;
  return <Navigate to="/dashboard/intervention" replace />;
}

function ChefRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "chef_poste") return <Navigate to="/dashboard" replace />;
  return children;
}

function InterventionRoute({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  if (user?.role === "chef_poste") return <Navigate to="/dashboard/chef" replace />;
  if (user?.role === "gardiennage" && !user?.allow_ronde) return <Navigate to="/dashboard/gardiennage" replace />;
  return children;
}

function NoClient({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/dashboard/mes-sites" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.platform_role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function SpaceOwnerRoute({ children }) {
  const { user } = useAuth();
  if (!user?.multi_space_owner || user?.agent_mode) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function FlagRoute({ flag, managers, children }) {
  const { user } = useAuth();
  const ok = user?.role === "admin" || (user?.role === "manager" && (managers || user?.[flag])) || (flag === "acces_tva" && user?.role === "secretaire" && user?.acces_tva);
  return ok ? children : <Navigate to="/dashboard" replace />;
}

function ClientRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "client") return <Navigate to="/dashboard" replace />;
  return children;
}

function ManagerRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin" && user?.role !== "manager") return <Navigate to="/dashboard" replace />;
  return children;
}

function ExpRoute({ children }) {
  const { user } = useAuth();
  return user?.role === "manager" && user?.acces_exploitation === false ? <Navigate to="/dashboard" replace /> : children;
}

function CompanyDocsRoute({ children }) {
  const { user } = useAuth();
  if (!user || !["admin", "manager", "secretaire", "chef_poste"].includes(user.role) || (user.role === "manager" && !user.acces_secretariat)) return <Navigate to="/dashboard" replace />;
  return children;
}

function FacturationRoute({ tab, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/dashboard" replace />;
  const role = user.role;
  const ok = role === "admin" || role === "secretaire" ||
    ((role === "manager" || role === "demo") && (!Array.isArray(user.tab_permissions) || user.tab_permissions.includes(tab)));
  if (!ok) return <Navigate to="/dashboard" replace />;
  return children;
}

function ModulePlaceholder({ label }) {
  return (
    <div className="p-8" data-testid={`module-placeholder-${label.toLowerCase()}`}>
      <h1 className="text-2xl font-display font-semibold text-white mb-2">{label}</h1>
      <p className="text-zinc-400">Module en cours de configuration.</p>
    </div>
  );
}

function PTIRoute() {
  const { user } = useAuth();
  if (user?.role === "agent" || user?.role === "gardiennage") return <AgentPTIPage />;
  return <PTIPage />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 text-[#007AFF] animate-spin" /></div>}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/guide" element={<Suspense fallback={null}><GuidePage /></Suspense>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/presentation" element={<Suspense fallback={null}><PresentationPage /></Suspense>} />
      <Route path="/support" element={<Suspense fallback={null}><SupportPage /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={null}><PresentationPage /></Suspense>} />
      <Route path="/devis/valider/:token" element={<ClientDevisValidationPage />} />
      <Route path="/contrat/signer/:token" element={<Suspense fallback={null}><ContratSignerPage /></Suspense>} />
      <Route path="/dashboard" element={<Protected><Layout /></Protected>}>
        <Route index element={<HomeRedirect />} />
        <Route path="entreprises" element={<SuperAdminRoute><EntreprisesPage /></SuperAdminRoute>} />
        <Route path="clients" element={<ModuleRoute module="clients"><SocietesPage /></ModuleRoute>} />
        <Route path="clients/:id" element={<ModuleRoute module="clients"><SocieteDetailPage /></ModuleRoute>} />
        <Route path="nouveaux" element={<ModuleRoute module="clients" tab="nouveaux_clients"><NouveauxClientsPage /></ModuleRoute>} />
        <Route path="suivi-cles" element={<ModuleRoute module="suivi_cles"><SuiviClesPage /></ModuleRoute>} />
        <Route path="points-nfc" element={<ExpRoute><NoClient><NfcPointsPage /></NoClient></ExpRoute>} />
        <Route path="dotations" element={<ExpRoute><NoClient><DotationsPage /></NoClient></ExpRoute>} />
        <Route path="pti" element={<ExpRoute><PTIRoute /></ExpRoute>} />
        <Route path="mes-dotations" element={<NoClient><MesDotationsPage /></NoClient>} />
        <Route path="vehicules" element={<ExpRoute><NoClient><VehiculesPage /></NoClient></ExpRoute>} />
        <Route path="carnet-de-bord" element={<NoClient><CarnetBordPage /></NoClient>} />
        <Route path="resilies" element={<ModuleRoute module="resilies"><ResiliesPage /></ModuleRoute>} />
        <Route path="cles-envoyees" element={<ModuleRoute module="cles_envoyees"><ClesEnvoyeesPage /></ModuleRoute>} />
        <Route path="consignes" element={<ModuleRoute module="consignes"><ConsignesPage /></ModuleRoute>} />
        <Route path="passation" element={<ModuleRoute module="passation"><PassationPage /></ModuleRoute>} />
        <Route path="intervention" element={<InterventionRoute><InterventionPage /></InterventionRoute>} />
        <Route path="gardiennage" element={<GardiennageRoute><GardiennagePage /></GardiennageRoute>} />
        <Route path="mod/gardiennage" element={<NoClient><GardiennagePage /></NoClient>} />
        <Route path="mod/intervention" element={<NoClient><ModInterventionPage /></NoClient>} />
        <Route path="mod/ronde" element={<NoClient><ModRondePage /></NoClient>} />
        <Route path="mes-missions" element={<NoClient><MesMissionsPage /></NoClient>} />
        <Route path="missions-cloturees" element={<GardiennageRoute><MissionsCloturees /></GardiennageRoute>} />
        <Route path="accueil" element={<GardiennageRoute><AgentHomePage /></GardiennageRoute>} />
        <Route path="comptes-rendus-interventions" element={<TabRoute tab="cr_interventions"><InterventionsPage mode="intervention" /></TabRoute>} />
        <Route path="comptes-rendus-gardiennage" element={<TabRoute tab="cr_gardiennage"><InterventionsPage mode="gardiennage" /></TabRoute>} />
        <Route path="comptes-rendus-rondes" element={<TabRoute tab="cr_interventions"><InterventionsPage key="ronde" mode="ronde" /></TabRoute>} />
        <Route path="comptes-rendus" element={<Navigate to="/dashboard/comptes-rendus-interventions" replace />} />
        <Route path="suivi-gardiennage" element={<TabRoute tab="suivi_gardiennage"><SuiviGardiennagePage /></TabRoute>} />
        <Route path="suivi-rondes" element={<ExpRoute><ManagerRoute><SuiviRondesPage /></ManagerRoute></ExpRoute>} />
        <Route path="rondes" element={<ExpRoute><ManagerRoute><RondesPage /></ManagerRoute></ExpRoute>} />
        <Route path="carnet-de-bord-suivi" element={<ExpRoute><ManagerRoute><CarnetBordManagerPage /></ManagerRoute></ExpRoute>} />
        <Route path="documents-societe" element={<CompanyDocsRoute><CompanyDocumentsPage /></CompanyDocsRoute>} />
        <Route path="controle-facture" element={<FacturationRoute tab="controle_facture"><FacturationMensuellePage /></FacturationRoute>} />
        <Route path="facturation-gardiennage" element={<FacturationRoute tab="facturation_gardiennage"><FacturationGardiennagePage /></FacturationRoute>} />
        <Route path="controle-devis" element={<FacturationRoute tab="controle_devis"><ControleDevisPage /></FacturationRoute>} />
        <Route path="decompte-paie" element={<TabRoute tab="decompte_paie"><PaiePage /></TabRoute>} />
        <Route path="paie-ccn" element={<TabRoute tab="paie_ccn"><PaieCcnPage /></TabRoute>} />
        <Route path="conges" element={<NoClient><CongesPage /></NoClient>} />
        <Route path="planning" element={<TabRoute tab="planning"><PlanningPage /></TabRoute>} />
        <Route path="devis" element={<DevisRoute><DevisPage /></DevisRoute>} />
        <Route path="prospection" element={<TabRoute tab="prospection"><ProspectionPage /></TabRoute>} />
        <Route path="notes-frais" element={<TabRoute tab="notes_frais"><NotesFraisPage /></TabRoute>} />
        <Route path="mes-notes-frais" element={<NoClient><MesNotesFraisPage /></NoClient>} />
        <Route path="reclamations" element={<SecRoute><ReclamationsPage /></SecRoute>} />
        <Route path="appels-offres" element={<MgrRoute><AppelsOffresPage /></MgrRoute>} />
        <Route path="mes-reclamations" element={<NoClient><MesReclamationsPage /></NoClient>} />
        <Route path="amendes" element={<SecRoute><AmendesPage /></SecRoute>} />
        <Route path="a-payer" element={<FlagRoute flag="acces_a_payer"><APayerPage /></FlagRoute>} />
        <Route path="tva" element={<FlagRoute flag="acces_tva"><TvaPage /></FlagRoute>} />
        <Route path="rapports-mensuels" element={<AdminRoute><RapportsMensuelsPage /></AdminRoute>} />
        <Route path="taches" element={<FlagRoute flag="acces_taches" managers><TachesPage /></FlagRoute>} />
        <Route path="rendez-vous" element={<MgrRoute><RendezVousPage /></MgrRoute>} />
        <Route path="mes-amendes" element={<NoClient><MesAmendesPage /></NoClient>} />
        <Route path="relances" element={<TabRoute tab="relances"><RelancesPage /></TabRoute>} />
        <Route path="assistant" element={<TabRoute tab="assistant"><AssistantPage /></TabRoute>} />
        <Route path="chef" element={<ChefRoute><ChefPostePage /></ChefRoute>} />
        <Route path="chef-agents" element={<ChefRoute><ChefAgentsPage /></ChefRoute>} />
        <Route path="agents" element={<TabRoute tab="agents"><AgentsPage /></TabRoute>} />
        <Route path="presence" element={<TabRoute tab="presence"><PresencePage /></TabRoute>} />
        <Route path="chefs-poste" element={<AdminRoute><ChefsPostePage /></AdminRoute>} />
        <Route path="compte-demo" element={<AdminRoute><DemoAccountPage /></AdminRoute>} />
        <Route path="journal-emails" element={<ManagerRoute><EmailLogsPage /></ManagerRoute>} />
        <Route path="notifications" element={<AdminRoute><NotificationsAdminPage /></AdminRoute>} />
        <Route path="managers" element={<AdminRoute><ManagersPage /></AdminRoute>} />
        <Route path="contrats" element={<AdminRoute><ContratsPage /></AdminRoute>} />
        <Route path="comptes-clients" element={<ExpRoute><ManagerRoute><ClientsPortailPage /></ManagerRoute></ExpRoute>} />
        <Route path="mes-sites" element={<ClientRoute><ClientPortailSitesPage /></ClientRoute>} />
        <Route path="mes-comptes-rendus" element={<ClientRoute><ClientComptesRendusPage /></ClientRoute>} />
        <Route path="mes-sites/:siteId" element={<ClientRoute><ClientSiteDetailPage /></ClientRoute>} />
        <Route path="soustraitants" element={<TabRoute tab="soustraitants"><SousTraitantsPage /></TabRoute>} />
        <Route path="messages" element={<NoClient><MessagesPage /></NoClient>} />
        <Route path="profil" element={<ProfilePage />} />
        <Route path="espace-entreprise" element={<AdminRoute><EspaceEntreprisePage /></AdminRoute>} />
        <Route path="espaces" element={<SpaceOwnerRoute><EspacesPage /></SpaceOwnerRoute>} />
        <Route path="suivi" element={<TabRoute tab="suivi"><AuditPage /></TabRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
